import React from 'react';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import BaseRichTextInput from 'Components/ComponentLibrary/Atoms/Inputs/BaseRichTextInput';
import PropTypes from 'prop-types';
import Paper from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Paper';
import classNames from 'classnames';
import {
  RichTextEditorInputFieldSkeleton,
} from 'Components/ComponentLibrary/Components/Uncategorized/RichTextEditorInputField/components/RichTextEditorInputFieldSkeleton';

const useStyles = buildUseStyles('RichTextField', (theme) => ({
  root: {
    width: '100%',
  },
  input: {
    flexGrow: 1,
  },
  inputRow: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  inlineToolbar: {
    display: 'flex',
    flexShrink: 0,
  },
  inlineToolbarHidden: {
    display: 'none',
  },
  inlineToolbarRounded: {
    paddingRight: theme.spacing(1),
  },
  bottomToolbar: {
    padding: theme.spacing(1),
    display: 'flex',
    justifyContent: 'flex-end',
  },
  bottomToolbarHidden: {
    display: 'none',
  },
  hidden: {
    display: 'none',
  },
  disabled: {
    backgroundColor: theme.palette.facet.mediumGray3,
  },
  rounded: {
    borderRadius: theme.radius.md,
  },
  roundedInput: {
    padding: theme.spacing(0, 1),
  },
}));

const RichTextField = ({
  bottomToolbar,
  inlineToolbar,
  value,
  onChange,
  inputMinHeight,
  inputMaxHeight,
  inputTest,
  placeholder,
  disabled,
  borderless,
  rounded,
  customConfig,
}) => {
  const [messageInputLoading, setMessageInputLoading] = React.useState(true);

  const classes = useStyles();
  const variant = borderless ? 'mui' : 'outlined';
  return (
    <>
      {messageInputLoading && <RichTextEditorInputFieldSkeleton height={customConfig?.height ?? inputMinHeight} />}
      <Paper
        className={classNames(classes.root, {
          [classes.rounded]: rounded,
          [classes.disabled]: disabled,
          [classes.hidden]: messageInputLoading,
        })}
        variant={variant}
      >
        <Box
          className={classes.inputRow}
        >
          <Box
            className={classNames(classes.input, {
              [classes.roundedInput]: rounded,
            })}
          >
            <BaseRichTextInput
              onChange={onChange}
              value={value}
              minHeight={inputMinHeight}
              maxHeight={inputMaxHeight}
              inputTest={inputTest}
              placeholder={placeholder}
              disabled={disabled}
              onLoaded={() => setMessageInputLoading(false)}
              customConfig={customConfig}
            />
          </Box>
          <Box
            className={classNames(classes.inlineToolbar, {
              [classes.inlineToolbarHidden]: !inlineToolbar,
              [classes.inlineToolbarRounded]: rounded,
            })}
          >
            {inlineToolbar}
          </Box>
        </Box>
        <Box
          className={classNames(classes.bottomToolbar, {
            [classes.bottomToolbarHidden]: !bottomToolbar,
          })}
        >
          {bottomToolbar}
        </Box>
      </Paper>
    </>
  );
};

RichTextField.defaultProps = {
  inputTest: undefined,
  inputMinHeight: undefined,
  inputMaxHeight: undefined,
  inlineToolbar: undefined,
  bottomToolbar: undefined,
  placeholder: '',
  disabled: false,
  rounded: false,
  borderless: false,
  customConfig: null,
};

RichTextField.propTypes = {
  inputMinHeight: PropTypes.number,
  inputMaxHeight: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  inputTest: PropTypes.string,
  inlineToolbar: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  bottomToolbar: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  rounded: PropTypes.bool,
  borderless: PropTypes.bool,
  customConfig: PropTypes.object,
};

export default RichTextField;
